/**
 * InfinitSales - Syncing Modal
 * Shows syncing status after WhatsApp connection
 * Similar to WhatsApp's "Sincronizando mensagens" screen
 */
import React from 'react';
import { RefreshCw, MessageCircle, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const SyncingModal = ({ isOpen, progress = null }) => {
  const { colors } = useTheme();

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      maxWidth: '400px',
      width: '90%',
      padding: '40px 32px',
      textAlign: 'center',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    iconContainer: {
      marginBottom: '24px',
      position: 'relative',
      display: 'inline-block',
    },
    icon: {
      color: colors.accentPrimary,
      animation: 'spin 2s linear infinite',
    },
    messageIcon: {
      position: 'absolute',
      bottom: '-4px',
      right: '-4px',
      backgroundColor: colors.bgPrimary,
      borderRadius: '50%',
      padding: '4px',
      color: colors.accentPrimary,
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: '12px',
      margin: '0 0 12px 0',
    },
    subtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
      marginBottom: '24px',
      lineHeight: '1.5',
    },
    warningBox: {
      backgroundColor: colors.bgTertiary,
      borderRadius: '12px',
      padding: '16px',
      marginTop: '20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      textAlign: 'left',
    },
    warningIcon: {
      color: colors.warning || '#f59e0b',
      flexShrink: 0,
      marginTop: '2px',
    },
    warningText: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: '1.5',
    },
    progressContainer: {
      marginTop: '20px',
    },
    progressBar: {
      width: '100%',
      height: '6px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '3px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.accentPrimary,
      borderRadius: '3px',
      transition: 'width 0.3s ease',
      animation: progress === null ? 'indeterminate 1.5s ease-in-out infinite' : 'none',
      width: progress !== null ? `${progress}%` : '30%',
    },
    progressText: {
      fontSize: '12px',
      color: colors.textTertiary,
      marginTop: '8px',
    },
    tip: {
      fontSize: '12px',
      color: colors.textTertiary,
      marginTop: '16px',
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.iconContainer}>
          <RefreshCw size={56} style={styles.icon} />
          <div style={styles.messageIcon}>
            <MessageCircle size={20} />
          </div>
        </div>

        <h2 style={styles.title}>Sincronizando...</h2>
        
        <p style={styles.subtitle}>
          Estamos buscando suas conversas e mensagens.
          <br />
          Isso pode levar alguns instantes.
        </p>

        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={styles.progressFill} />
          </div>
          <p style={styles.progressText}>
            {progress !== null ? `${progress}% concluído` : 'Carregando conversas...'}
          </p>
        </div>

        <div style={styles.warningBox}>
          <AlertCircle size={18} style={styles.warningIcon} />
          <p style={styles.warningText}>
            <strong>Não feche esta janela.</strong>
            <br />
            Algumas mensagens antigas podem não aparecer imediatamente. 
            Elas serão sincronizadas em segundo plano.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); width: 30%; }
          50% { transform: translateX(100%); width: 50%; }
          100% { transform: translateX(300%); width: 30%; }
        }
      `}</style>
    </div>
  );
};

export default SyncingModal;